import {
  Client,
  ClientLocation,
  PaginationData,
  ResourceInventoryEntry,
  ResourceInventorySummary,
  SeedBatch,
  SeedInventoryEntry,
  SeedInventorySummary,
  TrayInventoryEntry,
  TrayInventorySummary,
} from 'constants/interface';
import { CostCenterEnum, InventoryOutputTypeEnum, InventoryTypeEnum } from 'constants/inventory';
import { ResourceCategoryEnum } from 'constants/resource';
import {
  buildPaginationParams,
  getPaginationHeaders,
  PaginatedResponse,
  requestClient,
} from 'infra/request';

export interface CreateClientLocationData extends Omit<
  ClientLocation,
  'id' |
  'createdAt' |
  'updatedAt' |
  'createdBy' |
  'updatedBy'
> {}

export interface CreateClientData extends Omit<
  Client,
  'id' |
  'createdAt' |
  'updatedAt' |
  'createdBy' |
  'updatedBy'
> {
  locations: CreateClientLocationData[]
}

export interface UpdateClientData {
  id: string
  data: Omit<CreateClientData, 'locations'>
}

export interface UpdateClientLocationData {
  id: string
  data: CreateClientLocationData
}

export interface CreateClientResourceInputData {
  clientId: string
  data: {
    entryDate: string
    deliveredBy: string
    receivedBy: string
    fiscalNote?: string
    notes?: string
    items: {
      resourceId: string
      quantity: number
      price: number
    }[]
  }
}

export interface CreateClientResourceOutputData {
  clientId: string
  data: {
    entryDate: string
    costCenter: CostCenterEnum
    outputType: InventoryOutputTypeEnum
    notes?: string
    items: {
      resourceId: string
      quantity: number
    }[]
  }
}

export interface CreateClientResourceTransferData {
  clientId: string
  data: {
    entryDate: string
    sourceType: InventoryTypeEnum
    source: string
    notes?: string
    items: {
      resourceId: string
      quantity: number
      price: number
    }[]
  }
}

export interface GetClientResourceInventorySummaryList extends PaginationData {
  client?: string
  resource?: string
  category?: ResourceCategoryEnum
}

// TRAY

export interface CreateClientTrayInputData {
  clientId: string
  data: {
    entryDate: string
    deliveredBy: string
    receivedBy: string
    fiscalNote?: string
    notes?: string
    items: {
      trayId: string
      quantity: number
      price: number
    }[]
  }
}

export interface CreateClientTrayOutputData {
  clientId: string
  data: {
    entryDate: string
    costCenter: CostCenterEnum
    outputType: InventoryOutputTypeEnum
    notes?: string
    items: {
      trayId: string
      quantity: number
    }[]
  }
}

export interface CreateClientTrayTransferData {
  clientId: string
  data: {
    entryDate: string
    sourceType: InventoryTypeEnum
    source: string
    notes?: string
    items: {
      trayId: string
      quantity: number
      price: number
    }[]
  }
}

export interface GetClientTrayInventorySummaryList extends PaginationData {
  client?: string
  tray?: string
}

// VARIETY

export interface CreateClientVarietyInputData {
  clientId: string
  data: {
    entryDate: string
    deliveredBy: string
    receivedBy: string
    provider: string
    receivingTermCode: string
    fiscalNote?: string
    notes?: string
    items: {
      varietyId: string
      batch: string
      expiryDate: string
      germinationRate: number
      quantity: number
      price: number
    }[]
  }
}

export interface CreateClientVarietyOutputData {
  clientId: string
  data: {
    entryDate: string
    costCenter: CostCenterEnum
    outputType: InventoryOutputTypeEnum
    notes?: string
    items: {
      varietyId: string
      batchId: string
      quantity: number
    }[]
  }
}

export interface CreateClientVarietyTransferData {
  clientId: string
  data: {
    entryDate: string
    sourceType: InventoryTypeEnum
    source: string
    notes?: string
    items: {
      varietyId: string
      batchId: string
      quantity: number
      price: number
    }[]
  }
}

export interface GetClientSeedBatchesList {
  clientId: string
  varietyId: string
}

export interface GetClientSeedInventorySummaryList extends PaginationData {
  client?: string
  variety?: string
}

export interface ClientApi {
  create(clientData: CreateClientData): Promise<Client>
  update(clientData: UpdateClientData): Promise<Client>
  getList(filter: PaginationData): Promise<PaginatedResponse<Client>>

  createLocation(
    clientId: string,
    locationData: CreateClientLocationData,
  ): Promise<ClientLocation>
  updateLocation(locationData: UpdateClientLocationData): Promise<ClientLocation>
  getLocationsList(
    clientId: string,
    filter: PaginationData,
  ): Promise<PaginatedResponse<ClientLocation>>

  createClientResourceInput(
    resourceInputData: CreateClientResourceInputData,
  ): Promise<ResourceInventoryEntry>
  createClientResourceOutput(
    resourceOutputData: CreateClientResourceOutputData,
  ): Promise<ResourceInventoryEntry>
  createClientResourceTransfer(
    resourceTransferData: CreateClientResourceTransferData,
  ): Promise<ResourceInventoryEntry>
  getResourceInventorySummaryList(
    filterData: GetClientResourceInventorySummaryList,
  ): Promise<PaginatedResponse<ResourceInventorySummary>>

  createClientTrayInput(
    trayInputData: CreateClientTrayInputData,
  ): Promise<TrayInventoryEntry>
  createClientTrayOutput(
    trayOutputData: CreateClientTrayOutputData,
  ): Promise<TrayInventoryEntry>
  createClientTrayTransfer(
    trayTransferData: CreateClientTrayTransferData,
  ): Promise<TrayInventoryEntry>
  getTrayInventorySummaryList(
    filterData: GetClientTrayInventorySummaryList,
  ): Promise<PaginatedResponse<TrayInventorySummary>>

  createClientVarietyInput(
    varietyInputData: CreateClientVarietyInputData,
  ): Promise<SeedInventoryEntry>
  createClientVarietyOutput(
    varietyOutputData: CreateClientVarietyOutputData,
  ): Promise<SeedInventoryEntry>
  createClientVarietyTransfer(
    varietyTransferData: CreateClientVarietyTransferData,
  ): Promise<SeedInventoryEntry>
  getSeedBatchesList(
    requestData: GetClientSeedBatchesList,
  ): Promise<SeedBatch[]>
  getSeedInventorySummaryList(
    filterData: GetClientSeedInventorySummaryList,
  ): Promise<PaginatedResponse<SeedInventorySummary>>
}

const clientApiBuilder = (): ClientApi => {
  const create = async (clientData: CreateClientData): Promise<Client> => {
    const params: Record<string, unknown> = {
      ...clientData,
    };
    const result = await requestClient.post('/clients', params);

    const createdUser: Client = result.data as Client;
    return createdUser;
  };

  const update = async (clientData: UpdateClientData): Promise<Client> => {
    const { id: clientId, data } = clientData;
    const params: Record<string, unknown> = { ...data };
    const result = await requestClient.put(`/clients/${clientId}`, params);
    const updatedClient: Client = result.data as Client;
    return updatedClient;
  };

  const getList = async (filterData: PaginationData): Promise<PaginatedResponse<Client>> => {
    const params = buildPaginationParams(filterData);
    const result = await requestClient.get('/clients', params);
    const pagination = getPaginationHeaders(result);
    const clientsList: Client[] = result.data;
    return {
      pagination,
      data: clientsList,
    };
  };

  const createLocation = async (
    clientId: string,
    locationData: CreateClientLocationData,
  ): Promise<ClientLocation> => {
    const params: Record<string, unknown> = {
      ...locationData,
    };
    const result = await requestClient.post(`/clients/${clientId}/locations`, params);

    const createdLocation: ClientLocation = result.data as ClientLocation;
    return createdLocation;
  };

  const updateLocation = async (
    locationData: UpdateClientLocationData,
  ): Promise<ClientLocation> => {
    const { id: locationId, data } = locationData;
    const params: Record<string, unknown> = { ...data };
    const result = await requestClient.put(`/locations/${locationId}`, params);
    const updatedLocation: ClientLocation = result.data as ClientLocation;
    return updatedLocation;
  };

  const getLocationsList = async (
    clientId: string,
    filterData: PaginationData,
  ): Promise<PaginatedResponse<ClientLocation>> => {
    const params = buildPaginationParams(filterData);
    const result = await requestClient.get(`/clients/${clientId}/locations`, params);
    const pagination = getPaginationHeaders(result);
    const locationsList: ClientLocation[] = result.data;
    return {
      pagination,
      data: locationsList,
    };
  };

  const createClientResourceInput = async (
    resourceInputData: CreateClientResourceInputData,
  ): Promise<ResourceInventoryEntry> => {
    const { clientId, data } = resourceInputData;
    const params: Record<string, unknown> = { ...data };
    const result = await requestClient.post(
      `/clients/${clientId}/resources/input`,
      params,
    );
    const createdEntry: ResourceInventoryEntry = result.data as ResourceInventoryEntry;
    return createdEntry;
  };

  const createClientResourceOutput = async (
    resourceOutputData: CreateClientResourceOutputData,
  ): Promise<ResourceInventoryEntry> => {
    const { clientId, data } = resourceOutputData;
    const params: Record<string, unknown> = { ...data };
    const result = await requestClient.post(
      `/clients/${clientId}/resources/output`,
      params,
    );
    const createdEntry: ResourceInventoryEntry = result.data as ResourceInventoryEntry;
    return createdEntry;
  };

  const createClientResourceTransfer = async (
    resourceTransferData: CreateClientResourceTransferData,
  ): Promise<ResourceInventoryEntry> => {
    const { clientId, data } = resourceTransferData;
    const params: Record<string, unknown> = { ...data };
    const result = await requestClient.post(
      `/clients/${clientId}/resources/transfer`,
      params,
    );
    const createdEntry: ResourceInventoryEntry = result.data as ResourceInventoryEntry;
    return createdEntry;
  };

  const getResourceInventorySummaryList = async (
    filterData: GetClientResourceInventorySummaryList,
  ): Promise<PaginatedResponse<ResourceInventorySummary>> => {
    const params = buildPaginationParams(filterData);
    const result = await requestClient.get('/clients/resources', params);
    const pagination = getPaginationHeaders(result);
    const resourceInventorySummaryList: ResourceInventorySummary[] = result
      .data as ResourceInventorySummary[];
    return {
      pagination,
      data: resourceInventorySummaryList,
    };
  };

  // TRAY

  const createClientTrayInput = async (
    trayInputData: CreateClientTrayInputData,
  ): Promise<TrayInventoryEntry> => {
    const { clientId, data } = trayInputData;
    const params: Record<string, unknown> = { ...data };
    const result = await requestClient.post(
      `/clients/${clientId}/trays/input`,
      params,
    );
    const createdEntry: TrayInventoryEntry = result.data as TrayInventoryEntry;
    return createdEntry;
  };

  const createClientTrayOutput = async (
    trayOutputData: CreateClientTrayOutputData,
  ): Promise<TrayInventoryEntry> => {
    const { clientId, data } = trayOutputData;
    const params: Record<string, unknown> = { ...data };
    const result = await requestClient.post(
      `/clients/${clientId}/trays/output`,
      params,
    );
    const createdEntry: TrayInventoryEntry = result.data as TrayInventoryEntry;
    return createdEntry;
  };

  const createClientTrayTransfer = async (
    trayTransferData: CreateClientTrayTransferData,
  ): Promise<TrayInventoryEntry> => {
    const { clientId, data } = trayTransferData;
    const params: Record<string, unknown> = { ...data };
    const result = await requestClient.post(
      `/clients/${clientId}/trays/transfer`,
      params,
    );
    const createdEntry: TrayInventoryEntry = result.data as TrayInventoryEntry;
    return createdEntry;
  };

  const getTrayInventorySummaryList = async (
    filterData: GetClientTrayInventorySummaryList,
  ): Promise<PaginatedResponse<TrayInventorySummary>> => {
    const params = buildPaginationParams(filterData);
    const result = await requestClient.get('/clients/trays', params);
    const pagination = getPaginationHeaders(result);
    const trayInventorySummaryList: TrayInventorySummary[] = result
      .data as TrayInventorySummary[];
    return {
      pagination,
      data: trayInventorySummaryList,
    };
  };

  // VARIETY

  const createClientVarietyInput = async (
    varietyInputData: CreateClientVarietyInputData,
  ): Promise<SeedInventoryEntry> => {
    const { clientId, data } = varietyInputData;
    const params: Record<string, unknown> = { ...data };
    const result = await requestClient.post(
      `/clients/${clientId}/varieties/input`,
      params,
    );
    const createdEntry: SeedInventoryEntry = result.data as SeedInventoryEntry;
    return createdEntry;
  };

  const createClientVarietyOutput = async (
    varietyOutputData: CreateClientVarietyOutputData,
  ): Promise<SeedInventoryEntry> => {
    const { clientId, data } = varietyOutputData;
    const params: Record<string, unknown> = { ...data };
    const result = await requestClient.post(
      `/clients/${clientId}/varieties/output`,
      params,
    );
    const createdEntry: SeedInventoryEntry = result.data as SeedInventoryEntry;
    return createdEntry;
  };

  const createClientVarietyTransfer = async (
    varietyTransferData: CreateClientVarietyTransferData,
  ): Promise<SeedInventoryEntry> => {
    const { clientId, data } = varietyTransferData;
    const params: Record<string, unknown> = { ...data };
    const result = await requestClient.post(
      `/clients/${clientId}/varieties/transfer`,
      params,
    );
    const createdEntry: SeedInventoryEntry = result.data as SeedInventoryEntry;
    return createdEntry;
  };

  const getSeedBatchesList = async (
    requestData: GetClientSeedBatchesList,
  ): Promise<SeedBatch[]> => {
    const { varietyId, clientId } = requestData;
    const result = await requestClient.get(`clients/${clientId}/varieties/${varietyId}`);
    const resultData = result.data as SeedBatch[];
    return resultData;
  };

  const getSeedInventorySummaryList = async (
    filterData: GetClientSeedInventorySummaryList,
  ): Promise<PaginatedResponse<SeedInventorySummary>> => {
    const params = buildPaginationParams(filterData);
    const result = await requestClient.get('/clients/seeds', params);
    const pagination = getPaginationHeaders(result);
    const varietyInventorySummaryList: SeedInventorySummary[] = result
      .data as SeedInventorySummary[];
    return {
      pagination,
      data: varietyInventorySummaryList,
    };
  };

  return {
    create,
    update,
    getList,
    createLocation,
    updateLocation,
    getLocationsList,

    createClientResourceInput,
    createClientResourceOutput,
    createClientResourceTransfer,
    getResourceInventorySummaryList,

    createClientTrayInput,
    createClientTrayOutput,
    createClientTrayTransfer,
    getTrayInventorySummaryList,

    createClientVarietyInput,
    createClientVarietyOutput,
    createClientVarietyTransfer,
    getSeedBatchesList,
    getSeedInventorySummaryList,
  };
};

export const clientApi = clientApiBuilder();
